// ES module imports
import { init, h as snabbdomh, toVNode, attributesModule, styleModule, classModule, eventListenersModule, propsModule } from 'snabbdom';

// Inline pattern for class detection (avoiding constants.js dependency)
const CLASS_DEFINITION_PATTERN = /^\s*class\s+/;

// Initialize Snabbdom
const snabbdomInit = init;

// the node being currently rendered
let currentNode = null;
// this is the component state cache
let cache = {};
// this is the next cache that replace cache after a full re-render
let nextCache = {};

// ---------------------------------------------------------------------------
// Reactive subscription system
// Any reactive source (proxy, store, signal …) can call trackRead/notifyWrite.
// The runtime stays completely decoupled from the specific implementation.
// ---------------------------------------------------------------------------

// key (any stable string) → Set<Component>
const subscriptions = new Map();

/**
 * Call this inside a getter to register that the currently-rendering
 * component depends on `key`. No-op when called outside a render.
 */
function trackRead(key) {
  if (!currentNode || currentNode === rootNode) return;
  if (!subscriptions.has(key)) subscriptions.set(key, new Set());
  subscriptions.get(key).add(currentNode);
  currentNode.trackedKeys.add(key);
}

/**
 * Call this inside a setter/deleter to schedule re-renders for every
 * component that read a path related to `key` (exact match or ancestor/
 * descendant — matches the same prefix semantics as `hasChanged`).
 */
function notifyWrite(key) {
  const toRender = new Set();
  subscriptions.forEach((subs, subKey) => {
    if (
      subKey === key ||
      key.startsWith(subKey + '.') ||
      subKey.startsWith(key + '.')
    ) {
      subs.forEach(c => toRender.add(c));
    }
  });
  toRender.forEach(component => {
    if (!component.destroyed) scheduleRender(component);
  });
}

// eslint-disable-next-line arrow-body-style
const componentPath = (name, userKey) => {
  // Use userKey if provided for stable identity, otherwise use position
  const index = userKey !== undefined ? userKey : currentNode.componentsChildren.length;
  return currentNode
    ? `${currentNode.path}.${index}.${name}`
    : name;
};

class Component {
  constructor(ComponentFct, attributes, children, name) {
    if (ComponentFct === null) {
      if (!this.render) {
        throw new Error('Component should implement the render() method');
      }
      this.componentFct = this.render;
    } else {
      this.componentFct = ComponentFct;
    }
    this.attributes = attributes || {};
    this.children = children || [];
    this.name = name;
    if (name === 'root') {
      this.path = 'root';
    } else {
      this.path = componentPath(name);
    }
    this.componentsChildren = [];
    this.listeners = [];
    this.life = { mount: [], unmount: [], mountCleanup: [] };
    this.vnode = null;
    this.parent = currentNode;
    this.stateMap = {};
    this.contextMap = {};
    this.onChangeRegistry = {};
    this.mounted = false;
    this.destroyed = false;
    this.trackedKeys = new Set();
    cache[this.path] = this;
  }

  // called on a partial render
  partialRender() {
    // Don't render if component hasn't been initially mounted
    // Error happening on the CI
    if (!this.vnode) return;
    
    const parentNode = currentNode;
    currentNode = this;
    // Snapshot direct children before reset so we can destroy any that
    // are no longer present after the re-render.
    const prevChildren = new Map(this.componentsChildren.map(c => [c.path, c]));
    this._resetForRender();
    const newVnode = this.renderComponent();
    const thunk = patch(this.vnode, newVnode);
    copyToThunk(thunk, this.vnode);
    currentNode = parentNode;
    // Destroy components that were removed during this render.
    // Any descendant of a removed path `p` will have a cache key starting
    // with `p.`, so a single scan over the flat cache is sufficient —
    // no recursive tree walk needed.
    const newChildPaths = new Set(this.componentsChildren.map(c => c.path));
    const removedPaths = [];
    prevChildren.forEach((_, path) => {
      if (!newChildPaths.has(path)) removedPaths.push(path);
    });
    if (removedPaths.length > 0) {
      Object.keys(cache).forEach(cachedPath => {
        if (removedPaths.some(p => cachedPath === p || cachedPath.startsWith(p + '.'))) {
          cache[cachedPath]._destroy();
          delete cache[cachedPath];
        }
      });
    }
  }

  refresh() {
    if (this.destroyed) return;
    scheduleRender(this);
  }

  renderComponent() {
    try {
      const node = this.componentFct(this);
      this._checkOnChange();
      return node;
    } catch (e) {
      console.error(e);
      return h('span', {}, [e.message]);
    }
  }

  onMount() { return this; }

  onUnmount() { return this; }

  onChange(attribute, callback) {
    const value = this.attributes[attribute];
    this.onChangeRegistry[attribute] = { callback, value };
  }

  _checkOnChange() {
    Object.keys(this.onChangeRegistry).forEach((attribute) => {
      const record = this.onChangeRegistry[attribute];
      if (record.value !== this.attributes[attribute]) {
        record.value = this.attributes[attribute];
        record.callback();
      }
    });
  }

  mount(func) {
    if (this.mounted) return this;
    this.life.mount.push(func);
    return this;
  }

  unmount(func) {
    if (this.mounted) return this;
    this.life.unmount.push(func);
    return this;
  }

  state(name, initialValue) {
    this.stateMap[name] = this.stateMap[name] ?? initialValue;
    const value = this.stateMap[name];
    const setState = (newState) => {
      this.stateMap[name] = newState;
      scheduleRender(this);
    };
    return { value, setState, getState: () => this.stateMap[name] };
  }

  context(name, initialValue) {
    this.listeners[name] = [];
    if (initialValue && this.contextMap[name] === undefined) {
      this.contextMap[name] = initialValue;
    }
    const setContext = (value) => {
      this.contextMap[name] = value;
      this.listeners[name].forEach((node) => {
        scheduleRender(node);
      });
    };
    const getContext = () => {
      let node = this;
      const requestingNode = currentNode;
      while (node) {
        if (node.contextMap[name] !== undefined) {
          if (!node.listeners[name].includes(requestingNode) && requestingNode !== node) {
            node.listeners[name].push(requestingNode);
          }
          return node.contextMap[name];
        }
        node = node.parent;
      }
    };
    const value = initialValue || getContext();
    return { setContext, getContext, value };
  }

  _resetForRender() {
    this.componentsChildren = [];
    // Reset listeners to avoid stale references - components will re-register
    // during render if they still use the context
    this.listeners = [];
    // Clear reactive subscriptions — they will be re-established during the
    // upcoming render by trackRead calls in any reactive source.
    this.trackedKeys.forEach(key => subscriptions.get(key)?.delete(this));
    this.trackedKeys.clear();
  }

  _render(attributes, children) {
    const parentNode = currentNode;
    this.attributes = attributes;
    this.children = children;
    currentNode = this;
    this._resetForRender();
    const newVnode = this.renderComponent();
    if (!this.mounted) {
      this._mount();
    }
    parentNode && parentNode.componentsChildren.push(this);
    nextCache[this.path] = this;
    this.vnode = newVnode;
    currentNode = parentNode;
    return this.vnode;
  }

  _unmount() {
    this.onUnmount();
    // Call cleanup functions from mount in reverse order
    this.life.mountCleanup.reverse().forEach(fct => fct());
    this.life.mountCleanup = [];
    this.life.unmount.forEach(fct => fct());
    this.mounted = false;
    this.life.unmount = [];
  }

  _mount() {
    // do not mount in node
    if ((typeof process !== 'undefined' && process.title === 'node') || this.mounted) {
      return;
    }
    this.onMount();
    this.life.mount.forEach(fct => {
      const cleanup = fct();
      if (typeof cleanup === 'function') {
        this.life.mountCleanup.push(cleanup);
      }
    });
    this.mounted = true;
    this.life.mount = [];
  }

  _destroy() {
    this.destroyed = true;
    this._unmount();
    // Remove from all reactive subscriptions to avoid stale references.
    this.trackedKeys.forEach(key => subscriptions.get(key)?.delete(this));
    this.trackedKeys.clear();
    this.parent = null;
    this.children = [];
    this.stateMap = {};
    this.contextMap = {};
    this.componentsChildren = [];
  }
}

function isComponent(v) {
  return typeof v === 'function' && CLASS_DEFINITION_PATTERN.test(v.toString());
}

function createComponent(ComponentFct, attributes, children, name, userKey) {
  // Extract key from attributes if not provided as parameter
  const key = userKey !== undefined ? userKey : attributes.key;
  const path = componentPath(name, key);
  if (cache[path]) {
    return cache[path]._render(attributes, children);
  }
  let component;
  if (isComponent(ComponentFct)) {
    component = new ComponentFct(null, attributes, children, name);
  } else {
    component = new Component(ComponentFct, attributes, children, name);
  }

  return component._render(attributes, children);
}

function copyToThunk(vnode, thunk) {
  thunk.elm = vnode.elm;
  (vnode.data).fn = (thunk.data).fn;
  (vnode.data).args = (thunk.data).args;
  thunk.data = vnode.data;
  thunk.children = vnode.children;
  thunk.text = vnode.text;
  thunk.elm = vnode.elm;
}

function prepatch(oldVnode, newNode) {
  if (newNode.data.attrs.needRender === false) {
    copyToThunk(oldVnode, newNode);
  }
}

function h(name, attributes, children) {
  const attrs = {};
  let on;
  let style;
  let sclass;
  let hook = { prepatch };
  let key;
  let props;
  Object.entries(attributes).forEach((attr) => {
    const [index, value] = attr;
    if (index === 'on') {
      on = value;
    } else if (index === 'style') {
      style = value;
    } else if (index === 'key') {
      key = value;
    } else if (index === 'hooks') {
      hook = { ...hook, ...value };
    } else if (index === 'class') {
      if (typeof value === 'string') {
        attrs[index] = value;
      } else {
        sclass = value;
      }
    } else if (index === 'value' && (name === 'input' || name === 'textarea' || name === 'select')) {
      // Use props module for form element values
      if (!props) props = {};
      props[index] = value;
    } else if (index === 'innerHTML') {
      // Use props module to set innerHTML directly on the DOM element (for raw HTML injection)
      if (!props) props = {};
      props[index] = value;
    } else {
      attrs[index] = value;
    }
  });
  return snabbdomh(
    name,
    {
      on, style, attrs, hook, class: sclass, key, props,
    },
    children,
  );
}

const patch = snabbdomInit([
  attributesModule,
  styleModule,
  eventListenersModule,
  classModule,
  propsModule,
]);

let renderPipeline = [];
let animationRequest = false;

function scheduleRender(node) {
  renderPipeline.push(node);
  if (!animationRequest && globalThis.window) {
    animationRequest = true;
    window.requestAnimationFrame(() => {
      renderPipeline.forEach(node => node.partialRender());
      animationRequest = false;
      renderPipeline = [];
    });
  }
}

function destroyUnreferencedComponents() {
  // O(n) using Set for lookup instead of O(n²) with includes
  const nextCacheSet = new Set(Object.keys(nextCache));
  Object.keys(cache).forEach(path => {
    if (!nextCacheSet.has(path)) {
      cache[path]._destroy();
    }
  });
}

let rootNode = new Component(() => {}, {}, [], 'root');
currentNode = rootNode;

const newRoot = () => {
  rootNode = new Component(() => {}, {}, [], 'root');
  currentNode = rootNode;
};

/**
 * Render a component tree synchronously and return the root vnode.
 * Intended for server-side rendering — does not touch the DOM.
 * Pass the returned vnode to `renderToString()` from `blop-language/ssr`.
 */
function ssrRender(render) {
  cache = {};
  nextCache = {};
  newRoot();
  return render();
}

function mount(dom, render) {
  let vnode; let requested;
  cache = {};
  nextCache = {};
  function init() {
    const target = window.document.createElement('div');
    dom.innerHTML = '';
    dom.appendChild(target);
    newRoot();
    vnode = render();
    vnode = patch(toVNode(target), vnode);
    requested = false;
    return vnode;
  }
  function refresh(callback) {
    if (requested) {
      return;
    }
    requested = true;
    renderPipeline = [];
    const rerender = () => {
      let newVnode;
      nextCache = {};
      const now = (new Date()).getTime();
      try {
        newVnode = render();
        // nothing to update
        if (!newVnode) {
          requested = false;
          const after = (new Date()).getTime();
          callback && callback(after - now);
          return;
        }
        newRoot();
        // error can happen during patching
        patch(vnode, newVnode);
      } catch (error) {
        requested = false;
        throw error;
      }
      const after = (new Date()).getTime();
      vnode = newVnode;
      destroyUnreferencedComponents();
      cache = nextCache;
      requested = false;
      callback && callback(after - now);
    };
    window.requestAnimationFrame(() => {
      rerender();
      renderPipeline = [];
    });
  }
  return { refresh, init };
}

// ES module exports
export { h, patch, mount, Component, trackRead, notifyWrite, ssrRender };
export const c = createComponent;

/**
 * Resets the RAF-batch scheduling state. Only intended for use in tests:
 * the module-level `animationRequest` flag and `renderPipeline` are not
 * otherwise accessible, so stale state from one test can bleed into the next.
 */
export function __resetScheduler() {
  renderPipeline = [];
  animationRequest = false;
}
